import { API_BASE_URL } from "../config";
import { getSessionData } from "../../src/utils/helper";
import { ToastHelper } from "../utils/toastHelper";
import { getMockData } from "./mockData";

interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
  body?: any;
  signal?: AbortSignal;
}
const toastHelper = new ToastHelper();

const redirectToLogin = () => {
  sessionStorage.removeItem("sessionData");
  toastHelper.showToast({
    severity: "error",
    content: "Your session expired. Please login again.",
    options: {
      toastId: "fetchWrapper",
      position: "top-center",
      autoClose: 1000,
      onClose: () => (window.location.href = "/"),
    },
  });
};

const fetchWrapper = async (
  endpoint: string,
  options: FetchOptions = {}
): Promise<any> => {
  const isLocalDevelopment = process.env.REACT_APP_LOCAL_DEVELOPMENT === 'true';

  if (isLocalDevelopment) {
    console.log(`[LOCAL DEV] Mock call to: ${endpoint}`);
    return Promise.resolve(getMockData(endpoint, options));
  }
  const url = `${API_BASE_URL}${endpoint}`;
  const userInfo = getSessionData();

  const defaultHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${userInfo?.sessionIndex}`,
    "X-Authn-Instant": userInfo?.authInstance,
    "X-Userid": userInfo?.userId,
  };

  const config: RequestInit = {
    method: "GET",
    ...options,
    headers: { ...defaultHeaders, ...options.headers },
    signal: options.signal,
  };

  if (config.body && typeof config.body !== "string") {
    config.body = JSON.stringify(config.body);
  }

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      if (response.status === 401) {
        redirectToLogin();
        return null;
      }
      if (response.status === 403) {
        let data: any = null;
        try {
          data = await response.json();
        } catch {
          data = null;
        }
        const message = data?.message ?? data?.Message ?? "";
        if (message.includes("explicit deny")) {
          redirectToLogin();
          return null;
        }
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      } else
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    const contentType = response.headers.get("Content-Type");
    return contentType?.includes("application/json")
      ? await response.json()
      : await response.text();
  } catch (error: any) {
    if (error?.name === "AbortError") {
      console.log("Request was aborted");
      return;
    }
    console.error("Fetch Error:", error.message);
    throw error;
  }
};

export default fetchWrapper;
